// MyDelegation — every user can delegate their approvals during OOO
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, UserCheck, XCircle } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { API } from '../utils/api';

const MyDelegation = () => {
  const [users, setUsers] = useState([]);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ delegate_to: '', from_dt: '', to_dt: '', reason: '' });

  const fetchAll = async () => {
    try {
      const [uR, dR] = await Promise.all([axios.get(`${API}/users/directory`).catch(() => ({ data: [] })), axios.get(`${API}/delegations/me`)]);
      setUsers(uR.data || []);
      setCurrent(dR.data && dR.data.id ? dR.data : null);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const save = async () => {
    if (!form.delegate_to || !form.from_dt || !form.to_dt) { toast.error('Delegate + dates required'); return; }
    try {
      await axios.post(`${API}/delegations/me`, form);
      toast.success('Delegation active');
      setForm({ delegate_to: '', from_dt: '', to_dt: '', reason: '' });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const clear = async () => {
    if (!window.confirm('Clear active delegation?')) return;
    try { await axios.delete(`${API}/delegations/me`); toast.success('Cleared'); fetchAll(); }
    catch (err) { toast.error('Failed'); }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}><Loader2 className="w-8 h-8 animate-spin" /></div>;

  return (
    <div className="animate-fadeIn" data-testid="my-delegation-page">
      <div className="page-header">
        <h1 className="page-title"><UserCheck className="inline w-6 h-6 mr-2" /> My Delegation</h1>
        <p className="page-subtitle">Delegate your approvals when you're out of office</p>
      </div>

      {current ? (
        <div className="form-card" style={{ marginBottom: 24, borderLeft: '4px solid #27ae60' }}>
          <div style={{ padding: 20 }}>
            <div style={{ fontWeight: 600, fontSize: 14, color: '#27ae60', marginBottom: 8 }}>Delegation Active</div>
            <div style={{ fontSize: 13, marginBottom: 4 }}><b>Delegated to:</b> {current.delegate_name} ({current.delegate_email})</div>
            <div style={{ fontSize: 13, marginBottom: 4 }}><b>Window:</b> {new Date(current.from_dt).toLocaleString()} → {new Date(current.to_dt).toLocaleString()}</div>
            {current.reason && <div style={{ fontSize: 13, marginBottom: 8 }}><b>Reason:</b> {current.reason}</div>}
            <Button onClick={clear} variant="outline" data-testid="clear-delegation"><XCircle className="w-4 h-4 mr-1" />Clear Delegation</Button>
          </div>
        </div>
      ) : (
        <div className="form-card">
          <div style={{ padding: 20 }}>
            <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12 }}>Set New Delegation</div>
            <div style={{ display: 'grid', gap: 12 }}>
              <div>
                <Label>Delegate To</Label>
                <Select value={form.delegate_to} onValueChange={v => setForm({ ...form, delegate_to: v })}>
                  <SelectTrigger data-testid="delegate-to"><SelectValue placeholder="Select colleague" /></SelectTrigger>
                  <SelectContent>
                    {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name} ({u.role_label})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div><Label>From</Label><Input type="datetime-local" value={form.from_dt} onChange={e => setForm({ ...form, from_dt: e.target.value })} data-testid="delegate-from" /></div>
                <div><Label>To</Label><Input type="datetime-local" value={form.to_dt} onChange={e => setForm({ ...form, to_dt: e.target.value })} data-testid="delegate-to-dt" /></div>
              </div>
              <div><Label>Reason (optional)</Label><Input value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} placeholder="Vacation, off-site, etc." data-testid="delegate-reason" /></div>
              <Button onClick={save} data-testid="save-delegation">Save Delegation</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyDelegation;
